import { useState } from 'react';
import PropTypes from 'prop-types';
import FancyTimeStamp from './FancyTimeStamp';
import { useUserPresence } from '../hooks/useUserPresence';
import { getPresenceColor, getPresenceTitle } from '../utils/presenceUtils';
import './ContactList.css';

const ContactListItem = ({
  name = '',
  avatar = '',
  organization = '',
  lastActivityTimestamp = null,
  isSelected = false,
  isToggleItem = false,
  isToggled = false,
  onClick = () => {},
  presence = null,
  online = false,
  disabled = false,
  powerLevel = 0,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const presenceRecord = presence;
  const isOnline = online;

  const highlightState = (isSelected)
    ? 'selected'
    : ((isHovered && !disabled) ? 'hovered' : '');

  return (
    <div
      className={`contact-list-item ${highlightState} ${disabled ? 'disabled' : ''}`}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => !disabled && setIsHovered(false)}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
    >
      {
        (!isToggleItem || !isToggled) && <div className="contact-list-item-avatar">
          <img src={avatar} alt="Avatar" crossOrigin="anonymous" />
          <div
            className="contact-list-item-presence-indicator"
            style={{ backgroundColor: getPresenceColor(presenceRecord, isOnline) }}
            title={getPresenceTitle(presenceRecord, isOnline)}
          />
        </div>
      }
      {
        (isToggleItem && isToggled) && <div className="contact-list-item-check">
          <span className="material-icons">check</span>
        </div>
      }
      <div className="contact-list-item-info">
        <div className="contact-list-item-name">{name}</div>
        {
          powerLevel >= 100 && <div className="contact-list-item-admin">admin</div>
        }
        {
          powerLevel >= 50 && powerLevel < 100 && <div className="contact-list-item-admin">moderator</div>
        }
        {
          organization && <div className="contact-list-item-description">{organization}</div>
        }
      </div>
      {
        lastActivityTimestamp && (
          <div className="contact-list-item-timestamp">
            <FancyTimeStamp timestamp={lastActivityTimestamp} />
          </div>
        )
      }
    </div>
  );
};

ContactListItem.propTypes = {
  name: PropTypes.string,
  avatar: PropTypes.string,
  organization: PropTypes.string,
  lastActivityTimestamp: PropTypes.number,
  isSelected: PropTypes.bool,
  isToggleItem: PropTypes.bool,
  isToggled: PropTypes.bool,
  onClick: PropTypes.func,
  presence: PropTypes.shape({
    presence: PropTypes.string,
    currentlyActive: PropTypes.bool,
    lastActiveAgo: PropTypes.number,
    statusMsg: PropTypes.string,
    avatarUrl: PropTypes.string,
  }),
  online: PropTypes.bool,
  disabled: PropTypes.bool,
  powerLevel: PropTypes.number,
};

const ContactList = ({
  contacts = [],
  selectedContactId = '',
  emptyText = '',
  onSelectContact = () => {},
  isToggleList = false,
  isToggled = (_id) => false,
  disabled = (_id) => false,
}) => {
  const { getPresence, isOnline } = useUserPresence();

  return (
    <div className="contact-list">
      {
        contacts.length > 0 ? (
          contacts.map((contact) => {
            // Call getPresence and isOnline directly to avoid stale closure issues
            // Previously using useMemo caused callbacks to capture old presenceMap state
            const presence = getPresence(contact.id);
            const online = isOnline(contact.id);

            return (
              <ContactListItem
                key={contact.id}
                name={contact.name}
                avatar={contact.avatar}
                organization={contact.organization}
                lastActivityTimestamp={contact.lastActivityTimestamp}
                isSelected={selectedContactId === contact.id}
                isToggled={isToggled(contact.id)}
                isToggleItem={isToggleList}
                onClick={() => onSelectContact(contact.id)}
                presence={presence}
                online={online}
                disabled={disabled(contact.id)}
                powerLevel={contact.powerLevel || 0}
              />
            );
          })
        ) : (
          <div className="contact-list-empty-text">
            {emptyText}
          </div>
        )
      }
    </div>
  );
};

ContactList.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    avatar: PropTypes.string,
    organization: PropTypes.string,
    lastActivityTimestamp: PropTypes.number,
    powerLevel: PropTypes.number,
  })),
  selectedContactId: PropTypes.string,
  emptyText: PropTypes.string,
  onSelectContact: PropTypes.func,
  isToggleList: PropTypes.bool,
  isToggled: PropTypes.func,
  disabled: PropTypes.func,
};

export default ContactList;
